"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight, Building2, Loader2, Sparkles, Trophy } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { BrandAccordion, BrandPanel } from "@/components/branding/brand-panel";
import { LoginRole, RoleSelector, ROLE_OPTIONS } from "@/components/login/role-selector";
import { SchoolSearchInput } from "@/components/login/school-search-input";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { extractErrorMessage } from "@/lib/api-client";
import { landingPathForRole, roleLabel, useAuthStore } from "@/lib/auth-store";

const credentialsSchema = z.object({
  email: z.string().email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

const guardSchema = z.object({
  schoolCode: z.string().min(1, "Enter your school code"),
  email: z.string().email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

const signupSchema = z.object({
  name: z.string().min(2, "Enter your name"),
  email: z.string().email("Enter a valid email address"),
  password: z.string().min(6, "At least 6 characters"),
});

const DEMO_ACCOUNTS: Record<LoginRole, { role: string; email: string; password: string }[]> = {
  company: [{ role: "Super Admin", email: "super@playspace.in", password: "Super@123" }],
  school: [{ role: "School Admin", email: "admin@demo.playspace.in", password: "Admin@123" }],
  member: [
    { role: "Principal", email: "principal@demo.playspace.in", password: "Principal@123" },
    { role: "PE Staff", email: "coach@demo.playspace.in", password: "Coach@123" },
    { role: "Teacher", email: "teacher@demo.playspace.in", password: "Teach@123" },
    { role: "Staff", email: "staff@demo.playspace.in", password: "Staff@123" },
    { role: "Student", email: "student@demo.playspace.in", password: "Student@123" },
    { role: "Parent", email: "parent@demo.playspace.in", password: "Parent@123" },
  ],
  guard: [{ role: "Guard (school: DPS)", email: "guard@demo.playspace.in", password: "Guard@123" }],
  consumer: [{ role: "Consumer", email: "consumer@playspace.in", password: "Consumer@123" }],
};

export default function LoginPage() {
  const router = useRouter();
  const { login, guardLogin, consumerSignup, status, user, logout } = useAuthStore();
  const [role, setRole] = useState<LoginRole>("school");
  const [consumerTab, setConsumerTab] = useState<"login" | "signup">("login");

  const credentialsForm = useForm<z.infer<typeof credentialsSchema>>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { email: "", password: "" },
  });
  const guardForm = useForm<z.infer<typeof guardSchema>>({
    resolver: zodResolver(guardSchema),
    defaultValues: { schoolCode: "", email: "", password: "" },
  });
  const signupForm = useForm<z.infer<typeof signupSchema>>({
    resolver: zodResolver(signupSchema),
    defaultValues: { name: "", email: "", password: "" },
  });

  // If newly authenticated from this form, navigate to role landing page
  async function onCredentialsSubmit(values: z.infer<typeof credentialsSchema>) {
    try {
      await login(values.email, values.password);
      toast.success("Welcome back!");
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        router.push(landingPathForRole(currentUser.role));
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, "Login failed"));
    }
  }

  async function onGuardSubmit(values: z.infer<typeof guardSchema>) {
    try {
      await guardLogin(values.schoolCode, values.email, values.password);
      toast.success("Welcome back!");
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        router.push(landingPathForRole(currentUser.role));
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, "Login failed"));
    }
  }

  async function onSignupSubmit(values: z.infer<typeof signupSchema>) {
    try {
      await consumerSignup(values.name, values.email, values.password);
      toast.success("Account created!");
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        router.push(landingPathForRole(currentUser.role));
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, "Signup failed"));
    }
  }

  function fillDemo(email: string, password: string) {
    if (role === "guard") {
      guardForm.setValue("email", email);
      guardForm.setValue("password", password);
      guardForm.setValue("schoolCode", "DPS");
    } else {
      credentialsForm.setValue("email", email);
      credentialsForm.setValue("password", password);
    }
  }

  const activeRole = ROLE_OPTIONS.find((r) => r.id === role)!;

  return (
    <div className="grid min-h-screen w-full lg:grid-cols-[55%_45%]">
      <BrandPanel />

      <div className="flex flex-col justify-center gap-6 overflow-y-auto p-6 sm:p-10">
        <div className="mx-auto w-full max-w-md space-y-6">
          <div className="flex items-center gap-2 lg:hidden">
            <div className="flex size-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Trophy className="size-5" />
            </div>
            <span className="text-lg font-bold">PlaySpace Campus</span>
          </div>

          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">Welcome to PlayCampus</h1>
            <p className="text-sm text-muted-foreground">Choose how you&apos;d like to sign in.</p>
          </div>

          {/* Active Session Warning & Fast Switch */}
          {user && (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-primary uppercase tracking-wider">Active Session</span>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                  {roleLabel(user.role)}
                </span>
              </div>
              <p className="text-xs text-foreground">
                Signed in as <strong className="font-semibold">{user.name}</strong> ({user.email})
              </p>
              <div className="flex items-center gap-2 pt-1">
                <Button
                  size="sm"
                  onClick={() => router.push(landingPathForRole(user.role))}
                  className="flex-1 text-xs h-8"
                >
                  Go to {roleLabel(user.role)} Portal &rarr;
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    logout();
                    toast.info("Signed out. Select any demo persona below.");
                  }}
                  className="text-xs h-8 text-rose-600 hover:bg-rose-50 border-rose-200"
                >
                  Sign Out
                </Button>
              </div>
            </div>
          )}

          <RoleSelector value={role} onChange={setRole} />

          <div className="animate-in fade-in slide-in-from-bottom-1 space-y-4 duration-150" key={role}>
            {role !== "guard" && role !== "consumer" && (
              <Form {...credentialsForm}>
                <form onSubmit={credentialsForm.handleSubmit(onCredentialsSubmit)} className="space-y-4">
                  <FormField
                    control={credentialsForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input placeholder="you@school.edu" autoComplete="email" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={credentialsForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <Input type="password" autoComplete="current-password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" className="w-full" disabled={status === "loading"}>
                    {status === "loading" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Sign in as {activeRole.label}
                  </Button>
                </form>
              </Form>
            )}

            {role === "guard" && (
              <Form {...guardForm}>
                <form onSubmit={guardForm.handleSubmit(onGuardSubmit)} className="space-y-4">
                  <FormField
                    control={guardForm.control}
                    name="schoolCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Your school</FormLabel>
                        <FormControl>
                          <SchoolSearchInput value={field.value} onChange={field.onChange} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={guardForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Guard ID (email)</FormLabel>
                        <FormControl>
                          <Input placeholder="guard@school.edu" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={guardForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password / PIN</FormLabel>
                        <FormControl>
                          <Input type="password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" className="w-full" disabled={status === "loading"}>
                    {status === "loading" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Sign in as Guard
                  </Button>
                </form>
              </Form>
            )}

            {role === "consumer" && (
              <div className="space-y-4">
                <Tabs value={consumerTab} onValueChange={(v) => setConsumerTab(v as typeof consumerTab)}>
                  <TabsList className="w-full">
                    <TabsTrigger value="login" className="flex-1">
                      Log in
                    </TabsTrigger>
                    <TabsTrigger value="signup" className="flex-1">
                      New here? Sign up
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                {consumerTab === "login" ? (
                  <Form {...credentialsForm}>
                    <form onSubmit={credentialsForm.handleSubmit(onCredentialsSubmit)} className="space-y-4">
                      <FormField
                        control={credentialsForm.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input placeholder="you@example.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={credentialsForm.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Password</FormLabel>
                            <FormControl>
                              <Input type="password" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button type="submit" className="w-full" disabled={status === "loading"}>
                        {status === "loading" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Log in
                      </Button>
                    </form>
                  </Form>
                ) : (
                  <Form {...signupForm}>
                    <form onSubmit={signupForm.handleSubmit(onSignupSubmit)} className="space-y-4">
                      <FormField
                        control={signupForm.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Full name</FormLabel>
                            <FormControl>
                              <Input placeholder="Your name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={signupForm.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input placeholder="you@example.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={signupForm.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Password</FormLabel>
                            <FormControl>
                              <Input type="password" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button type="submit" className="w-full" disabled={status === "loading"}>
                        {status === "loading" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Create account & explore
                      </Button>
                    </form>
                  </Form>
                )}
              </div>
            )}

            <div className="space-y-2 rounded-lg border bg-muted/40 p-3">
              <p className="text-xs font-medium text-muted-foreground">Quick demo logins for {activeRole.label}</p>
              <div className="grid grid-cols-2 gap-2">
                {DEMO_ACCOUNTS[role].map((acc) => (
                  <button
                    key={acc.email}
                    type="button"
                    onClick={() => fillDemo(acc.email, acc.password)}
                    className="rounded-md border bg-background px-2 py-1.5 text-left text-xs transition hover:border-primary hover:text-primary"
                  >
                    {acc.role}
                  </button>
                ))}
              </div>
            </div>

            {/* Enterprise Campus Onboarding Banner */}
            <div className="rounded-xl border border-emerald-500/30 bg-gradient-to-r from-emerald-500/10 via-teal-500/5 to-transparent p-4 flex items-center justify-between gap-3 shadow-sm">
              <div className="space-y-0.5">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                  <Sparkles className="size-3.5" />
                  <span>Onboard a New School / Campus</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Launch the 4-step wizard to provision sports infrastructure, houses, and admin accounts.
                </p>
              </div>
              <Button
                size="sm"
                variant="default"
                onClick={() => router.push("/onboard")}
                className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0 text-xs shadow-sm h-8 flex items-center gap-1.5"
              >
                <span>Onboard</span>
                <ArrowRight className="size-3" />
              </Button>
            </div>
          </div>

          <BrandAccordion />
        </div>
      </div>
    </div>
  );
}
